import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
// ────────────────────────────────────────────────────────────────────────────
// Queries
// ────────────────────────────────────────────────────────────────────────────
export const friendsKeys = {
    list: ['friends', 'list'],
    pending: ['friends', 'pending'],
    search: (q) => ['friends', 'search', q],
    status: (otherId) => ['friends', 'status', otherId],
};
/** Liste des amis acceptés du joueur courant. */
export function useFriends() {
    const { user, status } = useAuth();
    return useQuery({
        queryKey: friendsKeys.list,
        enabled: status === 'ready' && !!user?.id,
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_friends');
            if (error)
                throw error;
            return (data ?? []);
        },
    });
}
/** Demandes pending entrantes + sortantes. */
export function useFriendRequests() {
    const { user, status } = useAuth();
    return useQuery({
        queryKey: friendsKeys.pending,
        enabled: status === 'ready' && !!user?.id,
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_pending_friend_requests');
            if (error)
                throw error;
            return (data ?? []);
        },
    });
}
/** Recherche fuzzy par pseudo (>= 2 chars). */
export function useSearchProfiles(query) {
    const trimmed = query.trim();
    return useQuery({
        queryKey: friendsKeys.search(trimmed.toLowerCase()),
        enabled: trimmed.length >= 2,
        staleTime: 10_000,
        queryFn: async () => {
            const { data, error } = await supabase.rpc('search_profiles', {
                p_query: trimmed,
                p_limit: 20,
            });
            if (error)
                throw error;
            return (data ?? []);
        },
    });
}
/** Statut de la relation entre auth.uid() et un autre joueur. */
export function useFriendshipStatus(otherId) {
    return useQuery({
        queryKey: otherId ? friendsKeys.status(otherId) : ['friends', 'status', 'none'],
        enabled: !!otherId,
        queryFn: async () => {
            if (!otherId)
                return 'none';
            const { data, error } = await supabase.rpc('get_friendship_status', { p_other_id: otherId });
            if (error)
                throw error;
            return (data ?? 'none');
        },
    });
}
// ────────────────────────────────────────────────────────────────────────────
// Mutations
// ────────────────────────────────────────────────────────────────────────────
function invalidateFriends(qc, otherId) {
    void qc.invalidateQueries({ queryKey: friendsKeys.list });
    void qc.invalidateQueries({ queryKey: friendsKeys.pending });
    if (otherId)
        void qc.invalidateQueries({ queryKey: friendsKeys.status(otherId) });
}
export function useSendFriendRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ pseudo, otherId }) => {
            const { error } = await supabase.rpc('send_friend_request', { p_addressee_pseudo: pseudo });
            if (error)
                throw error;
            // pseudo → on n'a pas forcément l'id, on invalide le status si fourni.
            if (otherId) {
                await qc.invalidateQueries({ queryKey: friendsKeys.status(otherId) });
            }
        },
        onSuccess: (_d, vars) => invalidateFriends(qc, vars.otherId),
    });
}
export function useAcceptFriendRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (requesterId) => {
            const { error } = await supabase.rpc('accept_friend_request', { p_requester_id: requesterId });
            if (error)
                throw error;
        },
        onSuccess: (_d, requesterId) => invalidateFriends(qc, requesterId),
    });
}
export function useDeclineFriendRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (requesterId) => {
            const { error } = await supabase.rpc('decline_friend_request', { p_requester_id: requesterId });
            if (error)
                throw error;
        },
        onSuccess: (_d, requesterId) => invalidateFriends(qc, requesterId),
    });
}
export function useRemoveFriend() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (otherId) => {
            const { error } = await supabase.rpc('remove_friend', { p_other_id: otherId });
            if (error)
                throw error;
        },
        onSuccess: (_d, otherId) => invalidateFriends(qc, otherId),
    });
}
